/**
 * MobileInsertSheet Component
 * 
 * Bottom sheet for adding new blocks to the journal.
 * Contains block type options and configuration flows.
 */

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import './mobile.css';

export type BlockType = 'note' | 'youtube' | 'quiz' | 'pdf' | 'explainer';

interface BlockConfig {
    youtubeUrl?: string;
    quizTopic?: string;
    quizCount?: number;
    quizDifficulty?: 'easy' | 'medium' | 'hard';
    explainerTopic?: string;
    pdfFile?: File;
}

interface MobileInsertSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectType: (type: BlockType, config?: BlockConfig) => void;
}

type SheetState = 'select' | 'youtube' | 'quiz' | 'explainer' | 'pdf';

const BLOCK_OPTIONS: { type: BlockType; label: string; description: string; needsConfig: boolean; icon: React.ReactNode }[] = [
    {
        type: 'note',
        label: 'Note',
        description: 'Add text notes and thoughts',
        needsConfig: false,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
        ),
    },
    {
        type: 'youtube',
        label: 'YouTube Video',
        description: 'Embed a video from YouTube',
        needsConfig: true,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
            </svg>
        ),
    },
    {
        type: 'quiz',
        label: 'Quiz',
        description: 'Generate a quiz to test knowledge',
        needsConfig: true,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
        ),
    },
    {
        type: 'pdf',
        label: 'PDF Document',
        description: 'Upload and view PDF files',
        needsConfig: true,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
            </svg>
        ),
    },
    {
        type: 'explainer',
        label: 'Explainer',
        description: 'Create an AI-powered explainer',
        needsConfig: true,
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
        ),
    },
];

const DIFFICULTY_OPTIONS = [
    { value: 'easy' as const, label: 'Easy' },
    { value: 'medium' as const, label: 'Medium' },
    { value: 'hard' as const, label: 'Hard' },
];

const COUNT_OPTIONS = [3, 5, 7, 10];

const MobileInsertSheet: React.FC<MobileInsertSheetProps> = ({
    isOpen,
    onClose,
    onSelectType,
}) => {
    const [sheetState, setSheetState] = useState<SheetState>('select');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [quizTopic, setQuizTopic] = useState('');
    const [quizCount, setQuizCount] = useState(5);
    const [quizDifficulty, setQuizDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
    const [explainerTopic, setExplainerTopic] = useState('');
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = (option: typeof BLOCK_OPTIONS[0]) => {
        if (option.needsConfig) {
            setSheetState(option.type as SheetState);
        } else {
            onSelectType(option.type);
            handleClose();
        }
    };

    const handleClose = () => {
        setSheetState('select');
        setYoutubeUrl('');
        setQuizTopic('');
        setQuizCount(5);
        setQuizDifficulty('medium');
        setExplainerTopic('');
        setPdfFile(null);
        onClose();
    };

    const handleBack = () => {
        setSheetState('select');
    };

    const handleSubmitYoutube = () => {
        if (!youtubeUrl.trim()) return;
        onSelectType('youtube', { youtubeUrl: youtubeUrl.trim() });
        handleClose();
    };

    const handleSubmitQuiz = () => {
        if (!quizTopic.trim()) return;
        onSelectType('quiz', {
            quizTopic: quizTopic.trim(),
            quizCount,
            quizDifficulty
        });
        handleClose();
    };

    const handleSubmitExplainer = () => {
        if (!explainerTopic.trim()) return;
        onSelectType('explainer', { explainerTopic: explainerTopic.trim() });
        handleClose();
    };

    const handleSubmitPdf = () => {
        if (!pdfFile) return;
        onSelectType('pdf', { pdfFile });
        handleClose();
    };

    const handlePdfFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type === 'application/pdf') {
            setPdfFile(file);
        }
    };

    // Config screens
    const renderYoutubeConfig = () => (
        <div className="mobile-insert-config">
            <button className="mobile-insert-back" onClick={handleBack}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
            </button>
            <div className="mobile-insert-config-header">
                <div className="mobile-insert-option-icon youtube">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                        <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
                    </svg>
                </div>
                <h3>Add YouTube Video</h3>
            </div>
            <div className="mobile-insert-config-body">
                <input
                    type="url"
                    placeholder="Paste YouTube URL..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    className="mobile-insert-input"
                    autoFocus
                />
            </div>
            <button
                className="mobile-insert-submit"
                onClick={handleSubmitYoutube}
                disabled={!youtubeUrl.trim()}
            >
                Add Video
            </button>
        </div>
    );

    const renderQuizConfig = () => (
        <div className="mobile-insert-config">
            <button className="mobile-insert-back" onClick={handleBack}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
            </button>
            <div className="mobile-insert-config-header">
                <div className="mobile-insert-option-icon quiz">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                </div>
                <h3>Generate Quiz</h3>
            </div>
            <div className="mobile-insert-config-body">
                <textarea
                    placeholder="What topic should the quiz cover?"
                    value={quizTopic}
                    onChange={(e) => setQuizTopic(e.target.value)}
                    className="mobile-insert-textarea"
                    rows={2}
                    autoFocus
                />

                {/* Question count */}
                <div className="mobile-insert-option-row">
                    <span className="mobile-insert-option-label">Questions</span>
                    <div className="mobile-insert-pills">
                        {COUNT_OPTIONS.map((count) => (
                            <button
                                key={count}
                                className={`mobile-insert-pill ${quizCount === count ? 'active' : ''}`}
                                onClick={() => setQuizCount(count)}
                            >
                                {count}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Difficulty */}
                <div className="mobile-insert-option-row">
                    <span className="mobile-insert-option-label">Difficulty</span>
                    <div className="mobile-insert-pills">
                        {DIFFICULTY_OPTIONS.map((diff) => (
                            <button
                                key={diff.value}
                                className={`mobile-insert-pill ${diff.value} ${quizDifficulty === diff.value ? 'active' : ''}`}
                                onClick={() => setQuizDifficulty(diff.value)}
                            >
                                {diff.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <button
                className="mobile-insert-submit"
                onClick={handleSubmitQuiz}
                disabled={!quizTopic.trim()}
            >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Generate Quiz'}
            </button>
        </div>
    );

    const renderExplainerConfig = () => (
        <div className="mobile-insert-config">
            <button className="mobile-insert-back" onClick={handleBack}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
            </button>
            <div className="mobile-insert-config-header">
                <div className="mobile-insert-option-icon explainer">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                </div>
                <h3>Create Explainer</h3>
            </div>
            <div className="mobile-insert-config-body">
                <textarea
                    placeholder="What topic should be explained?"
                    value={explainerTopic}
                    onChange={(e) => setExplainerTopic(e.target.value)}
                    className="mobile-insert-textarea"
                    rows={3}
                    autoFocus
                />
            </div>
            <button
                className="mobile-insert-submit"
                onClick={handleSubmitExplainer}
                disabled={!explainerTopic.trim()}
            >
                Create Explainer
            </button>
        </div>
    );

    return (
        <>
            {/* Overlay */}
            <div
                className={`mobile-sheet-overlay ${isOpen ? 'open' : ''}`}
                onClick={handleClose}
                aria-hidden="true"
            />

            {/* Sheet */}
            <div className={`mobile-sheet ${isOpen ? 'open' : ''}`}>
                {/* Drag handle */}
                <div className="mobile-sheet-handle" onClick={handleClose} />

                {sheetState === 'select' ? (
                    <>
                        {/* Header */}
                        <div className="px-5 pb-4">
                            <h2 className="text-lg font-semibold text-slate-900">Add to Journal</h2>
                            <p className="text-sm text-slate-500">Choose what to add</p>
                        </div>

                        {/* Block options */}
                        <div className="mobile-sheet-content">
                            <div className="flex flex-col gap-2">
                                {BLOCK_OPTIONS.map((option) => (
                                    <button
                                        key={option.type}
                                        className="mobile-insert-option"
                                        onClick={() => handleSelect(option)}
                                    >
                                        <div className={`mobile-insert-option-icon ${option.type}`}>
                                            {option.icon}
                                        </div>
                                        <div className="flex-1 text-left">
                                            <p className="text-sm font-medium text-slate-900">{option.label}</p>
                                            <p className="text-xs text-slate-500">{option.description}</p>
                                        </div>
                                        <svg className="w-5 h-5 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="mobile-sheet-content">
                        {sheetState === 'youtube' && renderYoutubeConfig()}
                        {sheetState === 'quiz' && renderQuizConfig()}
                        {sheetState === 'explainer' && renderExplainerConfig()}
                        {sheetState === 'pdf' && (
                            <div className="mobile-insert-config">
                                <button className="mobile-insert-back" onClick={handleBack}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15 18 9 12 15 6" />
                                    </svg>
                                    Back
                                </button>
                                <div className="mobile-insert-config-header">
                                    <div className="mobile-insert-option-icon pdf">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                    </div>
                                    <h3>Upload PDF</h3>
                                </div>
                                <div className="mobile-insert-config-body">
                                    <label className="mobile-pdf-upload">
                                        <input
                                            type="file"
                                            accept=".pdf,application/pdf"
                                            onChange={handlePdfFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        <div className="mobile-pdf-upload-box">
                                            {pdfFile ? (
                                                <>
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="check-icon">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                    <span>{pdfFile.name}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                        <polyline points="17 8 12 3 7 8" />
                                                        <line x1="12" y1="3" x2="12" y2="15" />
                                                    </svg>
                                                    <span>Tap to select PDF file</span>
                                                </>
                                            )}
                                        </div>
                                    </label>
                                </div>
                                <button
                                    className="mobile-insert-submit"
                                    onClick={handleSubmitPdf}
                                    disabled={!pdfFile}
                                >
                                    Upload PDF
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default MobileInsertSheet;
